import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CloudinaryImage } from './useCloudinaryUpload';

export interface GalleryCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  order: number;
  images: CloudinaryImage[];
}

export const useFirestoreGallery = () => {
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(collection(db, 'gallery'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      const categoriesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as GalleryCategory[];

      setCategories(categoriesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch gallery');
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const updateCategoryImages = async (
    categoryId: string,
    images: CloudinaryImage[]
  ) => {
    try {
      const categoryRef = doc(db, 'gallery', categoryId);
      await updateDoc(categoryRef, { images });
      await fetchCategories(); // Refresh the list
    } catch (err) {
      console.error('Error updating category images:', err);
      throw err;
    }
  };

  const addImagesToCategory = async (
    categoryId: string,
    newImages: CloudinaryImage[]
  ) => {
    try {
      const category = categories.find((c) => c.id === categoryId);
      if (!category) throw new Error('Category not found');

      const updatedImages = [...category.images, ...newImages];
      await updateCategoryImages(categoryId, updatedImages);
    } catch (err) {
      console.error('Error adding images:', err);
      throw err;
    }
  };

  const removeImageFromCategory = async (
    categoryId: string,
    publicId: string
  ) => {
    try {
      const category = categories.find((c) => c.id === categoryId);
      if (!category) throw new Error('Category not found');

      const updatedImages = category.images.filter(
        (img) => img.public_id !== publicId
      );
      await updateCategoryImages(categoryId, updatedImages);
    } catch (err) {
      console.error('Error removing image:', err);
      throw err;
    }
  };

  return {
    categories,
    loading,
    error,
    updateCategoryImages,
    addImagesToCategory,
    removeImageFromCategory,
    refreshCategories: fetchCategories,
  };
};
